import type { CSSProperties, ReactNode } from 'react';

/**
 * Generic playable-card primitive — a real trading card, not a div.
 *
 * The frame is the headline. The OUTER border is a brushed-metal FOIL coloured
 * by RARITY (gold legendary … iron trash) with a matching exterior glow and a
 * corner ribbon naming the tier — that's the restored rarity coding. The INNER
 * face is bone card-stock (paper grain, letterpress). A gloss sheen sweeps the
 * high tiers. Children own the inside strips; POSITION lives on the nameplate
 * (the guernsey colour), so rarity and position never fight for the same pixel.
 */
export type CardSize = 'sm' | 'md' | 'lg' | 'fluid';
export type CardKind = 'football' | 'player' | 'action' | 'weather' | 'stadium' | 'generic';
export type CardAccent = 'slate' | 'indigo' | 'amber' | 'red' | 'emerald';
export type Rarity = 'legendary' | 'epic' | 'rare' | 'uncommon' | 'common' | 'trash';

export type CardProps = {
  size?: CardSize;
  kind?: CardKind;
  /** Rarity → the foil frame, the glow and the corner ribbon. */
  rarity?: Rarity;
  /** Legacy free accent for non-rarity cards (the football token). */
  accent?: CardAccent;
  /** Position "guernsey" colour, exposed to children as `--ec-accent`. */
  accentColor?: string;
  faceDown?: boolean;
  selected?: boolean;
  /** Has at least one legal play right now — lit foil edge. */
  playable?: boolean;
  className?: string;
  children?: ReactNode;
};

// Phone first. These had no step below `lg`, so a 112px-wide md card ate
// a third of a 360px screen and only two and a half of them fitted in the
// hand strip. Every size now starts smaller and grows with the viewport.
// `fluid` emits no width at all — the caller sizes it (the football token
// scales with the field it sits on).
const SIZE_CLASSES: Record<CardSize, string> = {
  sm: 'w-16 sm:w-20 lg:w-24',
  md: 'w-[5.5rem] sm:w-28 lg:w-32',
  lg: 'w-28 sm:w-36 lg:w-44',
  fluid: 'w-full',
};

const RARITY_CLASS: Record<Rarity, string> = {
  legendary: 'ec-r-legendary',
  epic: 'ec-r-epic',
  rare: 'ec-r-rare',
  uncommon: 'ec-r-uncommon',
  common: 'ec-r-common',
  trash: 'ec-r-trash',
};

/** Legacy accent enum → an identity colour (the football token uses these). */
const ACCENT_COLOR: Record<CardAccent, string> = {
  slate: 'var(--ec-pos-event)',
  indigo: 'var(--ec-pos-ruck)',
  red: 'var(--ec-pos-forward)',
  amber: 'var(--ec-brass)',
  emerald: 'var(--ec-pos-defence)',
};

/** Rarity → footer gem fill. */
const RARITY_GEM: Record<Rarity, string> = {
  legendary: 'fill-ec-brass',
  epic: 'fill-[#a06ed6]',
  rare: 'fill-[#80a8ce]',
  uncommon: 'fill-[#76b892]',
  common: 'fill-ec-ink-soft',
  trash: 'fill-ec-bone-dim',
};

export function Card({
  size = 'md',
  rarity,
  accent = 'slate',
  accentColor,
  faceDown = false,
  selected = false,
  playable = false,
  className = '',
  children,
}: CardProps) {
  const resolvedAccent = accentColor ?? ACCENT_COLOR[accent];
  const rarityClass = rarity ? RARITY_CLASS[rarity] : 'ec-r-common';

  const style: CSSProperties = { ['--ec-accent' as string]: resolvedAccent };

  return (
    <div
      style={style}
      className={[
        'ec-card is-interactive aspect-[2/3]',
        rarityClass,
        selected ? 'is-selected' : '',
        playable ? 'is-playable' : '',
        SIZE_CLASSES[size],
        className,
      ].join(' ')}
    >
      <div className="ec-card__face">{faceDown ? <CardBack /> : children}</div>
      {!faceDown && <span className="ec-card__holo" aria-hidden />}
      <span className="ec-card__sheen" aria-hidden />
    </div>
  );
}

function CardBack() {
  return (
    <div className="relative flex h-full w-full items-center justify-center bg-gradient-to-br from-ec-panel to-ec-ground">
      {/* Brass-on-ink crow monogram — the back of a heritage deck. */}
      <svg viewBox="0 0 40 60" className="h-3/4 w-3/4 text-ec-brass" aria-hidden>
        <rect x={3} y={3} width={34} height={54} rx={3} fill="none" stroke="currentColor" strokeWidth={0.8} opacity={0.5} />
        <rect x={5.5} y={5.5} width={29} height={49} rx={2} fill="none" stroke="currentColor" strokeWidth={0.4} opacity={0.3} />
        <path
          d="M13 34 q5 -9 12 -9 q-3 3 -2 6 q4 -1 6 2 q-4 1 -7 2 q-5 2 -9 -1 z"
          fill="currentColor"
          opacity={0.85}
        />
        <circle cx={25} cy={25} r={0.9} fill="var(--ec-ground)" />
        <text x={20} y={48} textAnchor="middle" fontSize={6} letterSpacing={1.5} fill="currentColor" fontFamily="var(--ec-font-display)" opacity={0.9}>
          ECG
        </text>
      </svg>
    </div>
  );
}

/** Small hexagonal gem indicating rarity. Caller sizes it via the wrapper. */
export function RarityGem({ rarity, className = '' }: { rarity: Rarity; className?: string }) {
  return (
    <svg viewBox="-10 -10 20 20" className={`h-3 w-3 ${className}`} aria-label={`${rarity} rarity`}>
      <polygon
        points="0,-8 7,-4 7,4 0,8 -7,4 -7,-4"
        className={RARITY_GEM[rarity]}
        stroke="rgba(19,22,27,0.5)"
        strokeWidth={0.8}
      />
    </svg>
  );
}
